/** @jsxImportSource @emotion/react */
import React, { useCallback, useMemo } from 'react'
import css from '@emotion/css/macro'
import { CloseOutlined, WarningOutlined } from '@ant-design/icons'
import { Modal } from 'antd'
import { Link } from 'react-router-dom'
import colors from '@sportninja/common/constants/appColors'
import Text from 'src/components/ts-components/Text'
import Button from 'src/components/ts-components/Button'
import useWindowSize from 'src/hooks/useWindowSize'
import { isCanlan } from '@sportninja/common/utils/customer-name'

interface RegistrationWarningModalProps {
  isOpen: boolean
  onClose: () => void
  onCloseParentModal?: () => void
  isLoading?: boolean
  scheduleId?: string
}

/**
 * Registration warning modal component that warns users about existing teams
 * when enabling registration for competitions
 */
const RegistrationWarningModal: React.FC<RegistrationWarningModalProps> = ({
  isOpen,
  onClose,
  onCloseParentModal,
  isLoading = false,
  scheduleId,
}) => {
  const { width } = useWindowSize()
  const isMobile = useMemo(() => width <= 768, [width])

  /**
   * Handles modal close and calls the onClose callback
   */
  const handleClose = useCallback(() => {
    if (!isLoading) {
      onClose()
    }
  }, [onClose, isLoading])

  /**
   * Renders the close icon
   */
  const closeIcon = useCallback(() => {
    return (
      <CloseOutlined
        css={css`
          color: ${colors.SOLITUDE};
          font-size: 18px;
          cursor: pointer;
          transition: all 0.2s;
          &:hover {
            color: ${colors.WHITE};
          }
        `}
      />
    )
  }, [])

  return (
    <Modal
      css={css`
        display: flex;
        flex: 1;
        height: ${isMobile ? '100vh' : 'auto'} !important;
        margin: ${isMobile ? '0' : '24px'};
        padding: 0;
        max-width: ${isMobile ? '100%' : '480px'};
        top: ${isMobile ? '0' : '50%'};
        .ant-modal-content {
          height: ${isMobile ? '100vh' : 'auto'} !important;
          border-radius: ${isMobile ? '0' : '16px'};
          display: flex;
          flex-direction: column;
          background: ${colors.SECONDARY_300};
          border: 1px solid ${colors.SECONDARY_100};
        }
        .ant-modal-body {
          flex: 1;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          padding: 24px;
        }
        .ant-modal-header {
          display: none;
        }
      `}
      width={isMobile ? '100%' : 480}
      closeIcon={closeIcon()}
      open={isOpen}
      onCancel={handleClose}
      centered={!isMobile}
      maskClosable={false}
      footer={null}
      styles={{
        content: {
          backgroundColor: colors.SECONDARY_300,
          padding: 0,
        },
        wrapper: {
          zIndex: 10002,
        },
        mask: {
          backdropFilter: 'blur(2px)',
          backgroundColor: 'rgba(6, 10, 16, 0.8)',
          zIndex: 10001,
        },
      }}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          justify-content: flex-start;
          align-items: center;
          gap: 16px;
          position: relative;
        `}
      >
        {/* Warning Icon */}
        <WarningOutlined
          css={css`
            font-size: 48px;
            color: ${colors.PRIMARY};
          `}
        />

        {/* Title */}
        <Text
          variant='heading1'
          weight='semiBold'
          color={colors.WHITE}
          style={`
            font-family: ${
              isCanlan
                ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
                : 'BarlowCondensed, Arial, Helvetica, sans-serif;'
            };
            font-size: 36px;
            font-weight: 600;
            line-height: 36px;
            text-align: center;
          `}
        >
          Teams Already Exist
        </Text>

        {/* Description */}
        <div
          css={css`
            align-self: stretch;
            flex-direction: column;
            justify-content: flex-start;
            align-items: flex-start;
            gap: 8px;
            display: flex;
          `}
        >
          <div
            css={css`
              align-self: stretch;
              text-align: center;
            `}
          >
            <Text
              variant='paragraphMedium'
              weight='regular'
              color={colors.WHITE}
            >
              Before Enabling Registration, Please Remove Any Existing Teams
              From The{' '}
              <Link
                to={`/schedule/${scheduleId}/setup`}
                onClick={() => {
                  onClose()
                  onCloseParentModal?.()
                }}
                css={css`
                  color: ${colors.PRIMARY};
                  font-weight: 600;
                  text-decoration: none;
                  &:hover {
                    text-decoration: underline;
                  }
                `}
              >
                Setup Tab
              </Link>{' '}
              Of This Competition. Once Registration Is Active, You Can Re-Add
              Teams Directly Through The Registration Portal.
            </Text>
          </div>
        </div>

        {/* Separator */}
        <div
          css={css`
            width: 100%;
            height: 1px;
            background-color: ${colors.SECONDARY_100};
            margin-top: 8px;
          `}
        />

        {/* Cancel Button */}
        <div
          css={css`
            width: 100%;
            display: flex;
            justify-content: center;
            margin-top: 16px;
          `}
        >
          <Button
            text='Cancel'
            onClick={handleClose}
            variant='secondary'
            isLoading={isLoading}
            style={css`
              max-width: 200px;
            `}
          />
        </div>
      </div>
    </Modal>
  )
}

export default RegistrationWarningModal
